// services/googleAuth.ts — Google Identity Services SDK loader + helpers.
//
// Dynamically loads the GIS script and provides idempotent init/render
// functions for the Google sign-in button.

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: string;
              theme?: string;
              size?: string;
              width?: number;
              text?: string;
              shape?: string;
              logo_alignment?: string;
            },
          ) => void;
        };
      };
    };
  }
}

let scriptLoaded = false;
let scriptLoading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptLoaded && window.google?.accounts) return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise<void>((resolve, reject) => {
    if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      scriptLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(script);
  });

  return scriptLoading;
}

let _callback: ((credential: string) => void) | null = null;

export async function initGoogleSignIn(
  clientId: string,
  callback: (credential: string) => void,
): Promise<void> {
  _callback = callback;
  await loadScript();
  window.google!.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      if (_callback && response.credential) {
        _callback(response.credential);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });
}

export function renderGoogleButton(container: HTMLElement): void {
  if (!window.google?.accounts) return;
  window.google.accounts.id.renderButton(container, {
    type: "standard",
    theme: "outline",
    size: "large",
    width: container.offsetWidth || 300,
    text: "signin_with",
    shape: "rectangular",
    logo_alignment: "left",
  });
}
